<template>
  <nav class="app-navbar">
    <div class="app-navbar__wrapper gap-4">
      <NuxtLink :to="localePath('/')">
        <LayoutBrandLogo
          id="branded-logo"
          :height="'2rem'"
          class="max-w-[186px] w-full app-navbar-logo-default"
        />
      </NuxtLink>
      <div class="app-navbar__burger-btn">
        <div class="border-r flex items-center">
          <div
            ref="bellContainerRef"
            :class="{ active: isActive('/notifications') }"
          >
            <button
              tabindex="0"
              class="flex flex-row items-center gap-2"
              @click="bellIconClicked()"
              @dblclick="bellDblClick"
            >
              <span class="material-symbols-outlined app-navbar__nav--icon"
                >notifications</span
              >
              <!-- Notification count-->
              <span
                v-show="notificationStore?.count > 0"
                class="border border-primary rounded-full text-sm px-2.5 py-0.5 ml-auto"
                >{{ notificationStore?.count }}</span
              >
            </button>
            <LayoutNotifications
              v-if="notificationsDropdownOpen"
              class="fixed top-[72px] right-0"
              @close="notificationsDropdownOpen = false"
            />
          </div>
          <!-- Dropdown -->
          <div class="app-navbar__dropdown m-1">
            <button
              tabindex="0"
              class="cursor-pointer text-primary font-bold mr-2"
              :id="'onboarding-desk2'"
            >
              {{ $t('Activity') }}
            </button>
            <ul
              tabindex="0"
              class="app-navbar__dropdown__menu dropdown-content bg-white min-w-max"
            >
              <li
                class="app-navbar__dropdown__item app-navbar__dropdown__item__title"
              >
                <NuxtLink
                  :to="localePath('/activity/recognition')"
                  class="app-navbar__link"
                >
                  {{ $t('Recognitions') }}
                </NuxtLink>
                <NuxtLink
                  :to="localePath('/activity/up-download')"
                  class="app-navbar__link"
                >
                  {{ $t('Up- & Downloads') }}
                </NuxtLink>
                <NuxtLink
                  :to="localePath('/data-lab')"
                  class="app-navbar__link"
                >
                  {{ $t('Trainings') }}
                </NuxtLink>
                <NuxtLink
                  :to="localePath('/activity/advanced')"
                  class="app-navbar__link"
                >
                  {{ $t('Advanced Operations') }}
                </NuxtLink>
                <NuxtLink
                  v-if="hasHermesAccess"
                  :to="localePath('/activity/hermes')"
                  class="app-navbar__link"
                >
                  {{ $t('Hermes') }}
                </NuxtLink>
                <NuxtLink
                  v-if="userProfile?.IsAdmin"
                  :to="localePath('/activity/all')"
                  class="app-navbar__link"
                >
                  {{ $t('All Jobs') }}
                </NuxtLink>
              </li>
            </ul>
          </div>
        </div>
        <div class="pl-3 z-20" data-testid="burger-menu">
          <label class="text-2xl cursor-pointer" for="nav-drawer">
            <i class="mdi mdi-menu" v-if="!drawerOpen"></i>
            <i class="mdi mdi-close" v-else></i>
          </label>
        </div>
      </div>
      <!-- Mobile: Menu -->
      <div
        class="app-navbar__drawer drawer drawer-end"
        :class="drawerOpen ? 'pointer-events-auto' : 'pointer-events-none'"
      >
        <input
          id="nav-drawer"
          type="checkbox"
          class="app-navbar__drawer__toggle"
          v-model="drawerOpen"
        />
        <div class="drawer-side">
          <label for="nav-drawer" class="drawer-overlay"></label>
          <ul
            class="app-navbar__drawer__menu app-navbar__drawer__menu--mobile rounded"
          >
            <li class="app-navbar__drawer__menu__item" :id="'onboarding-desk3'">
              <span class="!pb-0">
                {{ $t('Signed in as') }}
              </span>
              <strong> {{ userProfile?.Email }}</strong>
            </li>
            <hr class="w-full border-border-secondary my-1" />
            <li
              class="app-navbar__drawer__menu__item"
              v-for="(navbarItem, index) in navbarItems"
            >
              <NuxtLink
                v-if="!navbarItem.children"
                @click="handleClick()"
                :to="localePath(navbarItem.path)"
                class="app-navbar__link"
                :class="{ active: isActive(navbarItem.path) }"
              >
                <!-- nav icon-->
                <span :id="navbarItem.id" class="material-symbols-outlined">{{
                  navbarItem.icon
                }}</span>
                <!-- nav text -->
                <span> {{ navbarItem.name }}</span>
              </NuxtLink>
              <!-- Dropdown menu -->
              <NuxtLink
                v-else
                :to="localePath(navbarItem.path)"
                class="app-navbar__link"
                :class="{ active: isActive(navbarItem.path) }"
              >
                <!-- nav icon-->
                <span
                  :id="navbarItem.id"
                  class="material-symbols-outlined app-navbar__nav--icon"
                  >{{ navbarItem.icon }}</span
                >
                <!-- nav text -->
                <span class="app-navbar__nav--text">
                  {{ navbarItem.name }}</span
                >
              </NuxtLink>
              <ul
                v-if="navbarItem.children"
                class="app-sidebar__submenu--transition app-sidebar__nav--text"
                :class="{
                  'app-sidebar__submenu--active': isActive(navbarItem.path),
                  'app-sidebar__submenu--inactive': !isActive(navbarItem.path),
                }"
              >
                <li v-for="subItem in navbarItem.children" :key="subItem.name">
                  <!-- 44px -->
                  <NuxtLink
                    :id="subItem.id"
                    @click="
                      handleDropdownClick({
                        path: subItem.path,
                        view: subItem.query?.view,
                      })
                    "
                    class="app-navbar__link"
                    :class="{
                      active: isActiveSub(subItem.path, subItem.query?.view),
                    }"
                  >
                    <!-- nav icon-->
                    <span
                      v-if="subItem.icon"
                      class="material-symbols-outlined app-sidebar__nav--icon"
                      >{{ subItem.icon }}</span
                    >
                    <!-- nav text -->
                    <span class="app-sidebar__nav--text">
                      {{ subItem.name }}</span
                    >
                  </NuxtLink>
                </li>
              </ul>
            </li>
            <hr class="w-full border-border-secondary my-1" />
            <li class="app-navbar__drawer__menu__item">
              <NuxtLink
                class="app-navbar__link"
                @click="handleClick()"
                :to="
                  localePath({
                    name: 'user-settings',
                    query: {
                      view: 'personal',
                    },
                  })
                "
              >
                <span class="material-symbols-outlined app-navbar__nav--icon"
                  >settings</span
                >
                <span>{{ $t('Settings') }}</span>
              </NuxtLink>
            </li>
            <hr class="w-full border-border-secondary my-1" />
            <li>
              <NuxtLink
                to="/invitations"
                @click="handleClick()"
                class="app-navbar__link"
              >
                <i class="mdi mdi-email"></i>
                <span>{{ $t('Invitations') }}</span>
              </NuxtLink>
            </li>
            <hr class="w-full border-border-secondary my-1" />
            <li class="app-navbar__drawer__menu__item">
              <NuxtLink
                class="app-navbar__link"
                @click="handleClick()"
                :to="
                  localePath({
                    name: 'changelog',
                  })
                "
              >
                <i class="mdi mdi-creation"></i>
                <span>{{ $t('Whats new') }}</span>
              </NuxtLink>
            </li>
            <li class="app-navbar__drawer__menu__item">
              <a @click="showPrivacySettings" class="app-navbar__link">
                <i class="mdi mdi-cookie"></i>
                <span>{{ $t('Privacy Settings') }}</span>
              </a>
            </li>
            <li>
              <button @click="helpModal" class="app-navbar__link">
                <i class="mdi mdi-help"></i>
                <span>{{ $t('Help') }}</span>
              </button>
            </li>
            <li class="app-navbar__drawer__menu__item">
              <NuxtLink
                @click="handleClick()"
                to="https://legal.transkribus.org/"
                target="_blank"
                class="app-navbar__link"
              >
                <i class="mdi mdi-scale-balance"></i>
                <span>{{ $t('Legal Information') }}</span>
              </NuxtLink>
            </li>
            <hr class="w-full border-border-secondary my-1" />
            <li class="app-navbar__dropdown__item--collapse">
              <input type="checkbox" />
              <div class="collapse-title">
                <i class="mdi mdi-earth"></i>
                <span>{{ $t('Language') }}</span>
              </div>
              <div class="collapse-content">
                <ul>
                  <li
                    v-for="(locale, index) in availableLocales"
                    :key="index"
                    :class="[
                      'app-navbar__item focus:bg-transparent',
                      {
                        'bg-background-tertiary': locale.code === currentLocale,
                      },
                    ]"
                  >
                    <span @click="setAndStoreLocale(locale.code)">
                      <nuxt-img
                        class="h-5 w-5"
                        loading="lazy"
                        :src="`/images/flags/${locale.label.toLowerCase()}.svg`"
                      />
                      <span class="is-uppercase">{{ locale.label }}</span>
                    </span>
                  </li>
                </ul>
              </div>
            </li>
            <hr class="w-full border-border-secondary my-1" />
            <li class="app-navbar__drawer__menu__item" data-testid="logout">
              <a @click="logout" class="app-navbar__link">
                <i class="mdi mdi-logout"></i>
                <span>{{ $t('Logout') }}</span>
              </a>
            </li>
            <li>
              <div class="px-2 empty-hidden">
                <DashboardCallToAction location="user-menu" />
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </nav>
</template>

<script lang="ts" setup>
import HelpModal from '~/components/Modals/Help.vue'
const { locale: currentLocale, setLocale, t } = useI18n()
const i18n = useI18n()
const availableLocales = i18n.locales
const { userProfile, keycloak } = useKeycloak()
const { $bus, $posthog, $features, $sentry }: any = useNuxtApp()
const notificationsApi = useNotifications()
const { hasAccess: hasHermesAccess } = useHermesAccess()
const router = useRouter()

const { hermesService } = useHermesService()

const userApi = useUser()
const route = useRoute()
const localePath = useLocalePath()
const configStore = useConfig()
const drawerOpen = ref<boolean>(false)
const notifications = ref<any>([])

const notificationsDropdownOpen = ref<boolean>(false)
const notificationStore = useNotificationsStore()
const bellContainerRef = ref<HTMLElement | null>(null)

onMounted(async () => {
  $bus.on('trialStart', captureTrialStart)
  $bus.on('upgrade', captureUpgrade)
  $bus.on('openDrawer', () => {
    drawerOpen.value = true
  })
  $bus.on('closeDrawer', () => {
    drawerOpen.value = false
  })

  if (userProfile?.Email) {
    $sentry.setUser({
      email: userProfile.Email,
    })
  }
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  $bus.off('trialStart', captureTrialStart)
  $bus.off('upgrade', captureUpgrade)
  $bus.off('openDrawer')
  $bus.off('closeDrawer')
  document.removeEventListener('click', handleClickOutside)
})

watch(
  () => notificationStore.count,
  () => {
    if (notificationsDropdownOpen.value) {
      getNotifications()
    }
  }
)

const showQualityControl = computed(() => {
  return (
    $features?.qc &&
    (userProfile.Email.endsWith('@transkribus.org') ||
      userProfile.Email.endsWith('@readcoop.eu'))
  )
})

const navbarItems = computed(() => {
  const baseItems = [
    {
      name: 'Home',
      path: '/',
      icon: 'home',
      id: 'onboarding-desk4',
    },
    {
      name: 'Desk',
      path: '/collection',
      icon: 'grid_view',
      id: 'onboarding-desk1',
    },
    {
      name: 'Models',
      path: '/models',
      icon: 'model_training',
      id: '',
      children: [
        {
          name: 'Public models',
          path: '/models',
        },
        {
          name: 'My models',
          path: '/my-models',
        },
        {
          name: 'Training lab',
          path: '/data-lab',
        },
      ],
    },
    {
      name: 'Sites',
      path: '/sites',
      icon: 'web',
      id: '',
      children: [
        {
          name: 'My sites',
          path: '/sites',
          query: { view: 'private' },
        },
        {
          name: 'Public sites',
          path: '/sites',
          query: { view: 'public' },
        },
        {
          name: 'World map',
          path: '/sites/map',
        },
      ],
    },
  ]

  baseItems[1].path = '/collection/overview'
  baseItems[1].children = [
    {
      name: 'Overview',
      path: '/collection/overview',
    },
    {
      name: 'Collections',
      path: '/collection',
    },
    ...(showQualityControl.value
      ? [{ name: 'Quality control', path: '/quality-control' }]
      : []),
  ]
  if (userProfile.IsAdmin) {
    baseItems[3].children.push({
      name: 'Admin',
      path: '/sites',
      query: { view: 'admin' },
    })
  }
  if ($features.dataSets) {
    baseItems.push({ name: 'Datasets', path: '/datasets', icon: 'data_table' })
  }
  if ($features.projects) {
    baseItems.push({ name: 'Projects', path: '/projects', icon: 'folder' })
  }
  return baseItems
})

const handleClickOutside = (event: MouseEvent) => {
  // Check if the click target is outside both the bell icon and dropdown
  const target = event.target as Node

  // Check if click is inside bell container
  const isClickInsideBell = bellContainerRef.value?.contains(target) || false

  // Only close if clicking outside both elements
  if (!isClickInsideBell) {
    notificationsDropdownOpen.value = false
  }
}

const currentPage = computed(() => {
  if (route.path.includes('collection')) return '/collection/overview'
  if (route.path.includes('training')) return '/training'
  if (route.path.includes('models')) return '/models'
  if (route.path.includes('my-models')) return '/my-models'
  if (route.path.includes('data-lab')) return '/models'
  if (route.path.includes('datasets')) return '/datasets'
  if (route.path.includes('sites')) return '/sites'
  if (route.path.includes('learn')) return '/learn'
  if (route.path.includes('quality-control')) return '/quality-control'
  if (
    route.path.includes('user-settings') ||
    route.path.includes('invitations') ||
    route.path.includes('changelog')
  )
    return '/user-settings'
  if (route.path.includes('activity')) return '/activity/all'
  if (route.path.includes('notifications')) return '/notifications'
  if (route.path.includes('projects')) return '/projects'
  else return '/'
})

const bellIconClicked = async () => {
  if (notificationsDropdownOpen.value) {
    notificationsDropdownOpen.value = false
    return
  }
  notificationsDropdownOpen.value = true
}

const bellDblClick = async () => {
  await notificationsApi.testNotification()
}

const helpModal = () => {
  drawerOpen.value = false
  const modal = {
    modalContent: {
      component: HelpModal,
    },
  }
  $bus.emit('open-modal', { modal: modal, props: { isMedium: true } })
}

function handleClick(): void {
  drawerOpen.value = false
}

function handleDropdownClick(options: { path: string; view?: string }) {
  drawerOpen.value = false
  if (options.view) {
    router.push({
      path: options.path,
      query: { view: options.view },
    })
  } else {
    router.push({
      path: options.path,
    })
  }
}

function isActiveSub(path: string, query: string): boolean {
  // Special handling for collection overview and collection pages
  if (path === '/collection/overview') {
    return route.path === '/collection/overview'
  }
  if (path === '/collection') {
    return (
      route.path.includes('/collection') &&
      route.path !== '/collection/overview'
    )
  }

  return route.path.includes(path) && route.query.view === query
}

function setAndStoreLocale(newLocale: string) {
  if (configStore.config == null) return
  configStore.config.locale = newLocale
  setLocale(newLocale)
  userApi.updateUserConfig({
    userId: userProfile?.UserId,
    config: { locale: newLocale },
  })
}

function isActive(path: string): boolean {
  return path === currentPage.value
}

function captureTrialStart() {
  $posthog.capture('upgrade:trial_start', {
    location: 'user_menu',
  })
}

function captureUpgrade() {
  $posthog.capture('upgrade:upgrade_now_click', {
    location: 'user_menu',
  })
}

const showPrivacySettings = () => {
  if ((window as any).UC_UI) {
    ;(window as any).UC_UI.showSecondLayer()
  }
  drawerOpen.value = false
}

function logout() {
  // Clear hermes cookies to prevent token mismatch when switching users
  hermesService.clearTokens()

  // Proceed with normal logout
  keycloak?.logout()
  $posthog.reset()
}

const getNotifications = async () => {
  notifications.value =
    (await notificationsApi.getAllNotifications({ limit: 100, offset: 0 })) ||
    []
}
</script>

<style>
.empty-hidden:empty {
  display: none;
}
</style>
